import { LogIn, LogOut, Package, Shield, Bell } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { logout } from '../../../store/slices/authSlice';
import { toast } from 'sonner';

export default function LoginIndicator() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isAdmin = user?.roles?.includes('Admin');
  const userName = user ? `${user.firstName} ${user.lastName}` : undefined;

  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    return 'U';
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleToggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleCloseDropdown = () => {
    setIsDropdownOpen(false);
  };

  const handleLogoutClick = () => {
    setIsDropdownOpen(false);
    dispatch(logout());
    toast.success('Logged out successfully');
    navigate('/');
  };

  if (!isAuthenticated) {
    return (
      <Link
        to="/login"
        className="flex items-center gap-2 px-2 sm:px-3 py-2 rounded-md hover:bg-neutral-100 transition-colors duration-fast"
        aria-label="Login"
      >
        <LogIn className="h-5 w-5 text-neutral-900" />
        <span className="hidden sm:inline text-sm font-medium text-neutral-900">Login</span>
      </Link>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggleDropdown}
        className="flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-md hover:bg-neutral-100 transition-colors duration-fast"
        aria-label="User account menu"
        aria-expanded={isDropdownOpen}
      >
        <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-brand-primary text-white flex items-center justify-center text-xs sm:text-sm font-medium">
          {getInitials()}
        </div>
        <span className="hidden md:inline text-sm font-medium text-neutral-900">
          {user?.firstName || 'Account'}
        </span>
      </button>

      {isDropdownOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-neutral-200 py-2 z-dropdown animate-slide-down">
          {/* User info header */}
          <div className="px-4 py-3 border-b border-neutral-200">
            <p className="text-sm font-medium text-neutral-900">{userName}</p>
            <p className="text-xs text-neutral-500">{user?.email}</p>
          </div>
          <Link
            to="/orders"
            onClick={handleCloseDropdown}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors duration-fast"
          >
            <Package className="h-4 w-4" />
            My Orders
          </Link>
          <Link
            to="/notifications"
            onClick={handleCloseDropdown}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-100 transition-colors duration-fast"
          >
            <Bell className="h-4 w-4" />
            Notifications
          </Link>
          {isAdmin && (
            <>
              <div className="border-t border-neutral-200 my-1" />
              <Link
                to="/admin"
                onClick={handleCloseDropdown}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-brand-primary hover:bg-brand-primary/10 transition-colors duration-fast"
              >
                <Shield className="h-4 w-4" />
                Admin Panel
              </Link>
            </>
          )}
          <div className="border-t border-neutral-200 my-1" />
          <button
            onClick={handleLogoutClick}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors duration-fast"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>
      )}
    </div>
  );
}
